import { type MouseEvent, useState } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { Menu, MenuItem, TableBody, TableRow } from "@mui/material";
import { CustomTableHeader, StyledTableCell, StyledTableWrapper } from "components/Common/Table/StyledTable.styles.ts";
import { UnavailableDataPlaceholderText } from "constants/text.ts";
import { SupportCase } from "features/Support/api/supportTypes.ts";
import { formatDateToMMDDYYYY } from "utils/date.ts";
import * as Styles from "./SupportCases.styles.ts";

interface SupportCasesTableProps {
	actionCaseId?: string;
	cases: SupportCase[];
	onCloseCase: (supportCase: SupportCase) => void;
	onEscalateCase: (supportCase: SupportCase) => void;
}

const formatSupportDate = (value?: string) => {
	if (!value) return UnavailableDataPlaceholderText;

	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? UnavailableDataPlaceholderText : formatDateToMMDDYYYY(date);
};

const SupportCasesTable = ({ actionCaseId, cases, onCloseCase, onEscalateCase }: SupportCasesTableProps) => {
	const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
	const [selectedCase, setSelectedCase] = useState<SupportCase>();

	const closeMenu = () => {
		setAnchorElement(null);
		setSelectedCase(undefined);
	};

	const openMenu = (event: MouseEvent<HTMLButtonElement>, supportCase: SupportCase) => {
		setAnchorElement(event.currentTarget);
		setSelectedCase(supportCase);
	};

	return (
		<Styles.CasesTableContainer>
			<StyledTableWrapper
				aria-label="Support cases"
				data-testid="support-cases-table"
				sx={{ minWidth: 960 }}
			>
				<CustomTableHeader>
					<TableRow>
						<StyledTableCell>Case #</StyledTableCell>
						<StyledTableCell>Order #</StyledTableCell>
						<StyledTableCell>Case Reason</StyledTableCell>
						<StyledTableCell>Subject</StyledTableCell>
						<StyledTableCell>Date Opened</StyledTableCell>
						<StyledTableCell>Status</StyledTableCell>
						<StyledTableCell>Last Modified</StyledTableCell>
						<StyledTableCell aria-label="Case actions" />
					</TableRow>
				</CustomTableHeader>

				<TableBody>
					{cases.map((supportCase, index) => {
						const caseKey = supportCase.id ?? supportCase.caseNumber ?? String(index);
						const hasCaseId = Boolean(supportCase.id);

						return (
							<TableRow
								data-testid={`support-case-${caseKey}`}
								key={caseKey}
							>
								<StyledTableCell>
									{supportCase.caseNumber ?? UnavailableDataPlaceholderText}
								</StyledTableCell>
								<StyledTableCell>
									{supportCase.orderNumber ?? UnavailableDataPlaceholderText}
								</StyledTableCell>
								<StyledTableCell>
									{supportCase.caseReason ?? UnavailableDataPlaceholderText}
								</StyledTableCell>
								<StyledTableCell>
									{supportCase.subject ?? UnavailableDataPlaceholderText}
								</StyledTableCell>
								<StyledTableCell>{formatSupportDate(supportCase.dateOpened)}</StyledTableCell>
								<StyledTableCell>
									<Styles.StatusChip
										label={supportCase.status ?? UnavailableDataPlaceholderText}
										size="small"
										status={supportCase.status}
										variant={supportCase.status === "Received" ? "outlined" : "filled"}
									/>
								</StyledTableCell>
								<StyledTableCell>{formatSupportDate(supportCase.lastModified)}</StyledTableCell>
								<StyledTableCell sx={{ minWidth: 44, width: 44 }}>
									{hasCaseId ? (
										<Styles.ActionButton
											aria-label={`Actions for case ${supportCase.caseNumber ?? "unknown"}`}
											disabled={actionCaseId === supportCase.id}
											onClick={(event) => openMenu(event, supportCase)}
											size="small"
										>
											<ArrowDropDownIcon />
										</Styles.ActionButton>
									) : (
										UnavailableDataPlaceholderText
									)}
								</StyledTableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</StyledTableWrapper>

			<Menu
				anchorEl={anchorElement}
				onClose={closeMenu}
				open={Boolean(anchorElement)}
			>
				<MenuItem
					onClick={() => {
						if (selectedCase) onEscalateCase(selectedCase);
						closeMenu();
					}}
				>
					Escalate Case
				</MenuItem>
				<Styles.CloseCaseMenuItem
					onClick={() => {
						if (selectedCase) onCloseCase(selectedCase);
						closeMenu();
					}}
				>
					Close Case
				</Styles.CloseCaseMenuItem>
			</Menu>
		</Styles.CasesTableContainer>
	);
};

export default SupportCasesTable;
